import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  SimpleGrid,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../layouts/AdminLayout';
import { Player, getPlayers, createPlayer, updatePlayer, deletePlayer } from '../../services/players';
import { uploadFile } from '../../services/articles';
import { translateNationality, getCountryFlag } from '../../utils/nationality';
import ThumbnailPreview from '../../components/common/ThumbnailPreview';
import { assetUrl } from '../../utils/url';
import UploadPanel, { UploadPanelFile } from '../../components/common/UploadPanel';

type Editing = Partial<Player> & { id?: number };

const PlayersAdminPage: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const toast = useToast();
  

  // Hoisted helper: convert country code to flag emoji
  function countryCodeToEmoji(cc: string) {
    if (!cc) return '';
    return cc.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
  }

  const uploadPlayerPhoto = async (file: File): Promise<UploadPanelFile> => {
    const res = file.type === 'image/svg+xml' ? await uploadFile(file) : await compressAndUpload(file);
    return {
      url: res.url,
      name: file.name,
      type: file.type || (res as any).type || 'image/*',
      size: file.size || (res as any).size || 0,
    };
  };

  // Countries for nationality dropdown
  const [countries, setCountries] = useState<Array<{ name: string; cca2: string; emoji: string }>>([]);
  // Search query for filtering nationality dropdown
  const [countryQuery, setCountryQuery] = useState<string>("");
  const countrySearchRef = useRef<HTMLInputElement | null>(null);
  const countrySelectRef = useRef<HTMLSelectElement | null>(null);
  // Simple fuzzy match scoring: higher is better
  function fuzzyScore(text: string, query: string): number {
    if (!query) return 0;
    const t = (text || '').toLowerCase();
    const q = (query || '').toLowerCase();
    // Exact and prefix bonuses
    if (t === q) return 1000;
    if (t.startsWith(q)) return 800 - (t.length - q.length);
    // Subsequence score
    let ti = 0, qi = 0, score = 0, streak = 0;
    while (ti < t.length && qi < q.length) {
      if (t[ti] === q[qi]) {
        streak += 1;
        score += 10 + streak; // reward consecutive matches
        qi++;
      } else {
        streak = 0;
      }
      ti++;
    }
    // Small penalty for distance
    if (qi === q.length) {
      return score - Math.abs(t.length - q.length);
    }
    return -Infinity; // not all chars matched in order
  }

  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim();
    if (!q) return countries;
    // Score by name and cca2 and take the better score
    const scored = countries
      .map((c) => ({
        c,
        s: Math.max(
          fuzzyScore(c.name, q),
          fuzzyScore(c.cca2, q)
        ),
      }))
      .filter((x) => x.s > -Infinity)
      .sort((a, b) => b.s - a.s || a.c.name.localeCompare(b.c.name, 'cs', { sensitivity: 'base' }))
      .map((x) => x.c);
    return scored;
  }, [countries, countryQuery]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Try v3.1 API first
        let json: any[] | null = null;
        const tryUrls = [
          'https://restcountries.com/v3.1/all?fields=name,cca2',
          // fallback: v2 API
          'https://restcountries.com/v2/all?fields=name,alpha2Code',
          // minimal fallback without fields (may be heavier, but used only if above fail)
          'https://restcountries.com/v3.1/all',
        ];
        let lastErr: any = null;
        for (const url of tryUrls) {
          try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`countries ${res.status}`);
            const data = await res.json();
            if (Array.isArray(data)) { json = data; break; }
          } catch (e) {
            lastErr = e;
          }
        }
        if (!mounted) return;
        if (!json) throw lastErr || new Error('countries fetch failed');
        const list = (json || []).map((c: any) => {
          const name = c?.name?.common || c?.name || '';
          const cca2 = c?.cca2 || c?.alpha2Code || '';
          return {
            name,
            cca2,
            emoji: countryCodeToEmoji(cca2),
          };
        }).filter((c: any) => c.name && c.cca2).sort((a: any, b: any) => a.name.localeCompare(b.name, 'cs', { sensitivity: 'base' }));
        setCountries(list);
      } catch (err) {
        // Hard fallback list for most common nationalities used locally
        const fallback = [
          { name: 'Czechia', cca2: 'CZ', emoji: countryCodeToEmoji('CZ') },
          { name: 'Slovakia', cca2: 'SK', emoji: countryCodeToEmoji('SK') },
          { name: 'Poland', cca2: 'PL', emoji: countryCodeToEmoji('PL') },
          { name: 'Germany', cca2: 'DE', emoji: countryCodeToEmoji('DE') },
          { name: 'Austria', cca2: 'AT', emoji: countryCodeToEmoji('AT') },
          { name: 'Ukraine', cca2: 'UA', emoji: countryCodeToEmoji('UA') },
          { name: 'France', cca2: 'FR', emoji: countryCodeToEmoji('FR') },
          { name: 'Spain', cca2: 'ES', emoji: countryCodeToEmoji('ES') },
          { name: 'Italy', cca2: 'IT', emoji: countryCodeToEmoji('IT') },
          { name: 'England', cca2: 'GB', emoji: countryCodeToEmoji('GB') },
        ];
        setCountries(fallback);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Persist countryQuery across sessions
  useEffect(() => {
    try {
      const saved = localStorage.getItem('playersAdmin.countryQuery');
      if (saved) setCountryQuery(saved);
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('playersAdmin.countryQuery', countryQuery);
    } catch {}
  }, [countryQuery]);

  // Helper: compress image client-side, resize to max dimension and convert to JPEG to reduce size
  const compressAndUpload = async (file: File) => {
    const img = await readFileAsImage(file);
    const maxDim = 1400;
    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, width, height);
    // Use JPEG for good compression; backend can accept jpeg (serving as png not mandatory)
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve as any, 'image/jpeg', 0.78));
    if (!blob) throw new Error('compression failed');
    const f = new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    return await uploadFile(f);
  };

  // Hoisted helper: read file into HTMLImageElement
  function readFileAsImage(file: File) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => {
        const img = new window.Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = fr.result as string;
      };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
  }

  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-players', { active: false }], queryFn: () => getPlayers({ active: false }) });

  const [editing, setEditing] = useState<Editing | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const JERSEY_MIN = 0;
  const HEIGHT_MIN = 0;
  const HEIGHT_MAX = 250;
  const WEIGHT_MIN = 0;
  const WEIGHT_MAX = 400;

  // Local state to persist partial DOB selections so the user sees what they picked
  const [dobParts, setDobParts] = useState<{ day: string; month: string; year: string }>({ day: '', month: '', year: '' });

  const openCreate = () => { setEditing({ first_name: '', last_name: '', is_active: true, email: '', phone: '', gender: '' } as any); setDobFromDateStr(''); onOpen(); };
  const openEdit = (p: Player) => { setEditing({ ...p }); setDobFromDateStr(p.date_of_birth || ''); onOpen(); };
  const closeModal = () => { setEditing(null); onClose(); };

  const createMut = useMutation({
    mutationFn: (payload: any) => createPlayer(payload),
    onSuccess: (created: any) => {
      try {
        qc.setQueryData(['admin-players', { active: false }], (old: any) => {
          const list = Array.isArray(old) ? old : (old?.data || []);
          const newList = [created, ...list];
          if (old && old.data) return { ...old, data: newList };
          return newList;
        });
      } catch (e) {}
      toast({ title: 'Hráč vytvořen', status: 'success' });
      qc.invalidateQueries({ queryKey: ['admin-players', { active: false }] });
      closeModal();
    },
    onError: (e: any) => {
      const status = e?.response?.status;
      const msg = e?.response?.data?.chyba || e?.response?.data?.error || e?.message || 'Chyba';
      toast({ title: 'Vytvoření selhalo', description: status ? `HTTP ${status}: ${msg}` : msg, status: 'error' });
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: any }) => updatePlayer(id, payload),
    onSuccess: () => { toast({ title: 'Hráč upraven', status: 'success' }); qc.invalidateQueries({ queryKey: ['admin-players', { active: false }] }); closeModal(); },
    onError: (e: any) => {
      const status = e?.response?.status;
      const msg = e?.response?.data?.chyba || e?.response?.data?.error || e?.message || 'Chyba';
      toast({ title: 'Aktualizace selhala', description: status ? `HTTP ${status}: ${msg}` : msg, status: 'error' });
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deletePlayer(id),
    onSuccess: () => { toast({ title: 'Hráč smazán', status: 'success' }); qc.invalidateQueries({ queryKey: ['admin-players', { active: false }] }); },
    onError: (e: any) => {
      const status = e?.response?.status;
      const msg = e?.response?.data?.chyba || e?.response?.data?.error || e?.message || 'Chyba';
      toast({ title: 'Smazání selhalo', description: status ? `HTTP ${status}: ${msg}` : msg, status: 'error' });
    },
  });

  const maybeSplitName = () => {
    setEditing((p) => {
      if (!p) return p;
      const fn = (p.first_name || '').trim();
      const ln = (p.last_name || '').trim();
      if (!ln && fn.includes(' ')) {
        const parts = fn.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
          return { ...(p as any), first_name: parts[0], last_name: parts[parts.length - 1] } as any;
        }
      }
      return p as any;
    });
  };

  const onSubmit = async () => {
    if (!editing) return;
    let fn = (editing.first_name || '').trim();
    let ln = (editing.last_name || '').trim();
    if (!ln && fn.includes(' ')) {
      const parts = fn.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        fn = parts[0];
        ln = parts[parts.length - 1];
      }
    }
    if (!fn || !ln) {
      toast({ title: 'Jméno a příjmení jsou povinné', status: 'warning' });
      return;
    }
    const tooBig = (
      typeof editing.height === 'number' && Number.isFinite(editing.height) && editing.height > HEIGHT_MAX
    ) || (
      typeof editing.weight === 'number' && Number.isFinite(editing.weight) && editing.weight > WEIGHT_MAX
    );
    if (tooBig) {
      toast({ title: 'Neplatná čísla', description: `Maxima: výška ${HEIGHT_MAX} cm, váha ${WEIGHT_MAX} kg`, status: 'warning' });
      return;
    }
    // Require date of birth: all three values must be selected
    if (!dobParts.day || !dobParts.month || !dobParts.year) {
      toast({ title: 'Datum narození je povinné', description: 'Vyberte den, měsíc i rok.', status: 'warning' });
      return;
    }
    // Build payload by including only present values to satisfy backend validation
    const payload: any = {
      first_name: fn,
      last_name: ln,
    };
    if (editing.date_of_birth) payload.date_of_birth = editing.date_of_birth;
    if (editing.position) payload.position = editing.position;
    if (typeof editing.jersey_number === 'number' && Number.isFinite(editing.jersey_number) && editing.jersey_number >= 0) {
      payload.jersey_number = editing.jersey_number;
    }
    if (editing.nationality) payload.nationality = editing.nationality;
    if (typeof editing.height === 'number' && Number.isFinite(editing.height) && editing.height > 0) {
      payload.height = editing.height;
    }
    if (typeof editing.weight === 'number' && Number.isFinite(editing.weight) && editing.weight > 0) {
      payload.weight = editing.weight;
    }
    if (editing.image_url) payload.image_url = editing.image_url;
    if ((editing as any).gender) payload.gender = (editing as any).gender;
    if (typeof editing.is_active === 'boolean') payload.is_active = editing.is_active;
    const email = ((editing as any).email || '').trim();
    const phone = ((editing as any).phone || '').trim();
    if (email) payload.email = email;
    if (phone) payload.phone = phone;
    try {
      if ((editing as any).id != null) {
        await updateMut.mutateAsync({ id: (editing as any).id, payload });
      } else {
        await createMut.mutateAsync(payload);
      }
    } catch (err) {
      // handled by mutation
    }
  };

  return (
    <AdminLayout requireAdmin={false}>
      <Heading size="lg" mb={4}>Hráči</Heading>

      <HStack mb={4}>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>Nový hráč</Button>
      </HStack>

      <Box bg={cardBg} borderWidth="1px" borderRadius="md" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th w="80px">Fotka</Th>
              <Th>Jméno</Th>
              <Th>Pozice</Th>
              <Th>Pohlaví</Th>
              <Th>Národnost</Th>
              <Th w="120px">Číslo</Th>
              <Th w="120px">Aktivní</Th>
              <Th w="160px">Akce</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (<Tr><Td colSpan={7}>Načítám...</Td></Tr>)}
            {!isLoading && (data || []).map((p) => (
              <Tr key={p.id} opacity={p.is_active ? 1 : 0.6}>
                <Td>
                  <ThumbnailPreview 
                    src={assetUrl(p.image_url) || '/dist/img/player-placeholder.svg'} 
                    alt={`${p.first_name} ${p.last_name}`} 
                    size="48px"
                    previewSize="300px"
                    borderRadius="md"
                  />
                </Td>
                <Td>{p.first_name} {p.last_name}</Td>
                <Td>{p.position || '-'}</Td>
                <Td>{p.gender ? (String(p.gender).toLowerCase() === 'women' ? 'Žena' : 'Muž') : '-'}</Td>
                <Td>
                  {p.nationality ? (
                    <HStack spacing={2}>
                      <span>{getCountryFlag(p.nationality)}</span>
                      <span>{translateNationality(p.nationality)}</span>
                    </HStack>
                  ) : '-'}
                </Td>
                <Td>{p.jersey_number ?? '-'}</Td>
                <Td><Switch isChecked={!!p.is_active} onChange={() => { if (p.id != null) updateMut.mutate({ id: p.id, payload: { is_active: !p.is_active } }); }} /></Td>
                <Td>
                  <HStack>
                    <IconButton aria-label="Upravit" size="sm" icon={<FiEdit2 />} onClick={() => openEdit(p)} />
                    <IconButton aria-label="Smazat" size="sm" colorScheme="red" icon={<FiTrash2 />} onClick={async () => { if (!confirm('Opravdu smazat záznam?')) return; if (p.id != null) await deleteMut.mutateAsync(p.id); }} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={closeModal} size="lg">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" overflowY="auto">
          <ModalHeader>{(editing as any)?.id ? 'Upravit hráče' : 'Nový hráč'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Jméno</FormLabel>
                  <Input value={editing?.first_name || ''} onChange={(e) => setEditing((p) => ({ ...(p as any), first_name: e.target.value }))} onBlur={maybeSplitName} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Příjmení</FormLabel>
                  <Input value={editing?.last_name || ''} onChange={(e) => setEditing((p) => ({ ...(p as any), last_name: e.target.value }))} />
                </FormControl>

                {/* Custom DOB picker: day / month / year (timezone-safe) */}
                <FormControl isRequired>
                  <FormLabel>Datum narození</FormLabel>
                  <HStack>
                    <Select value={dobParts.day} onChange={(e) => updateDobPart('day', e.target.value)}>
                      <option value="" disabled>Den</option>
                      {Array.from({ length: 31 }).map((_, i) => <option key={i+1} value={(i+1).toString()}>{i+1}</option>)}
                    </Select>
                    <Select value={dobParts.month} onChange={(e) => updateDobPart('month', e.target.value)}>
                      <option value="" disabled>Měsíc</option>
                      {Array.from({ length: 12 }).map((_, i) => <option key={i+1} value={(i+1).toString()}>{i+1}</option>)}
                    </Select>
                    <Select value={dobParts.year} onChange={(e) => updateDobPart('year', e.target.value)}>
                      <option value="" disabled>Rok</option>
                      {Array.from({ length: 80 }).map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={String(y)}>{y}</option>; })}
                    </Select>
                  </HStack>
                  <Box mt={2} fontSize="sm" color="gray.500">
                    {formatDobPreview(dobParts)}{(() => { const a = calculateAgeFromParts(dobParts); return a != null ? ` — ${a} ${czYears(a)}` : ''; })()}
                  </Box>
                </FormControl>

                <FormControl>
                  <FormLabel>Pozice</FormLabel>
                  <Select value={editing?.position || ''} onChange={(e) => setEditing((p) => ({ ...(p as any), position: e.target.value }))}>
                    <option value="">— vyberte pozici —</option>
                    <option value="Brankář">Brankář</option>
                    <option value="Obránce">Obránce</option>
                    <option value="Záložník">Záložník</option>
                    <option value="Útočník">Útočník</option>
                    <option value="Univerzál">Univerzál</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Pohlaví</FormLabel>
                  <Select value={(editing as any)?.gender || ''} onChange={(e) => setEditing((p) => ({ ...(p as any), gender: e.target.value }))}>
                    <option value="">— nevybráno —</option>
                    <option value="men">Muž</option>
                    <option value="women">Žena</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Číslo dresu</FormLabel>
                  <NumberInput min={JERSEY_MIN} keepWithinRange={false} clampValueOnBlur={false} value={typeof editing?.jersey_number === 'number' ? editing?.jersey_number : ''} onChange={(_, v) => setEditing((p) => ({ ...(p as any), jersey_number: Number.isFinite(v) && v >= 0 ? v : undefined }))}>
                    <NumberInputField inputMode="numeric" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Národnost</FormLabel>
                  <VStack align="stretch" spacing={2}>
                    <Input
                      placeholder="Hledat zemi (např. cz, czechia)…"
                      value={countryQuery}
                      onChange={(e) => setCountryQuery(e.target.value)}
                      ref={countrySearchRef}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          countrySelectRef.current?.focus();
                        } else if (e.key === 'Escape') {
                          setCountryQuery('');
                        } else if (e.key === 'Enter') {
                          if (filteredCountries.length === 1) {
                            const only = filteredCountries[0];
                            setEditing((p) => ({ ...(p as any), nationality: only.name }));
                            countrySelectRef.current?.focus();
                          }
                        }
                      }}
                    />
                    <Select
                      placeholder="— vyberte zemi —"
                      value={editing?.nationality || ''}
                      onChange={(e) => setEditing((p) => ({ ...(p as any), nationality: e.target.value }))}
                      ref={countrySelectRef}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' && (e.target as HTMLSelectElement).selectedIndex === 0) {
                          // Move back to search
                          e.preventDefault();
                          countrySearchRef.current?.focus();
                        }
                      }}
                    >
                      {filteredCountries.map((c) => (
                        <option key={c.cca2} value={c.name}>
                          {c.emoji} {translateNationality(c.name)} ({c.name})
                        </option>
                      ))}
                    </Select>
                  </VStack>
                </FormControl>

                <FormControl isInvalid={typeof editing?.height === 'number' && (editing?.height as number) > HEIGHT_MAX}>
                  <FormLabel>Výška (cm)</FormLabel>
                  <NumberInput min={HEIGHT_MIN} max={HEIGHT_MAX} keepWithinRange={false} clampValueOnBlur={false} value={typeof editing?.height === 'number' ? editing?.height : ''} onChange={(_, v) => setEditing((p) => ({ ...(p as any), height: Number.isFinite(v) ? v : undefined }))}>
                    <NumberInputField inputMode="numeric" />
                  </NumberInput>
                  <FormErrorMessage>Maximální výška je {HEIGHT_MAX} cm.</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={typeof editing?.weight === 'number' && (editing?.weight as number) > WEIGHT_MAX}>
                  <FormLabel>Váha (kg)</FormLabel>
                  <NumberInput min={WEIGHT_MIN} max={WEIGHT_MAX} keepWithinRange={false} clampValueOnBlur={false} value={typeof editing?.weight === 'number' ? editing?.weight : ''} onChange={(_, v) => setEditing((p) => ({ ...(p as any), weight: Number.isFinite(v) ? v : undefined }))}>
                    <NumberInputField inputMode="numeric" />
                  </NumberInput>
                  <FormErrorMessage>Maximální váha je {WEIGHT_MAX} kg.</FormErrorMessage>
                </FormControl>
                {/* Optional contact info (not shown publicly) */}
                <FormControl>
                  <FormLabel>Email (nepovinné)</FormLabel>
                  <Input type="email" value={(editing as any)?.email || ''} onChange={(e) => setEditing((p) => ({ ...(p as any), email: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Telefon (nepovinné)</FormLabel>
                  <Input
                    type="tel"
                    inputMode="tel"
                    value={(editing as any)?.phone || ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9+\s()-]/g, '');
                      setEditing((p) => ({ ...(p as any), phone: v }));
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key.length === 1 &&
                        /[^0-9+\s()-]/.test(e.key) &&
                        !e.ctrlKey && !e.metaKey && !e.altKey
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData('text');
                      const sanitized = pasted.replace(/[^0-9+\s()-]/g, '');
                      if (pasted !== sanitized) {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const start = input.selectionStart || 0;
                        const end = input.selectionEnd || 0;
                        const current = input.value;
                        const next = current.slice(0, start) + sanitized + current.slice(end);
                        setEditing((p) => ({ ...(p as any), phone: next }));
                        requestAnimationFrame(() => {
                          try { input.setSelectionRange(start + sanitized.length, start + sanitized.length); } catch {}
                        });
                      }
                    }}
                  />
                </FormControl>
                <Box gridColumn="1 / -1" fontSize="sm" color="gray.600">
                  Upozornění: telefonní číslo a e‑mail budou viditelné na hlavní stránce. Údaje nejsou povinné — pokud je nechcete zadávat, ponechte je prázdné.
                </Box>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Fotka</FormLabel>
                <HStack align="flex-start" spacing={3}>
                  <Image src={assetUrl(editing?.image_url) || '/player-placeholder.svg'} alt="photo" boxSize="56px" objectFit="cover" borderRadius="md" fallbackSrc="/dist/img/logo-club-empty.svg" />
                  <Box flex="1">
                    <UploadPanel
                      label="Nahrát fotku hráče"
                      description="Nahrajte fotku hráče (portrét)."
                      accept="image/*,image/svg+xml"
                      multiple={false}
                      maxFiles={1}
                      uploadFn={uploadPlayerPhoto}
                      allowUrlImport={false}
                      onUploadFinished={(uploaded) => {
                        const last = uploaded[uploaded.length - 1];
                        if (!last) return;
                        setEditing((p) => ({ ...(p as any), image_url: last.url }));
                      }}
                    />
                  </Box>
                </HStack>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Aktivní</FormLabel>
                <Switch isChecked={!!editing?.is_active} onChange={(e) => setEditing((p) => ({ ...(p as any), is_active: e.target.checked }))} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeModal}>Zrušit</Button>
            <Button colorScheme="blue" onClick={onSubmit} isLoading={createMut.isLoading || updateMut.isLoading}>Uložit</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );

  // Helpers for DOB to avoid timezone shifts
  function parseYmd(s?: string | null): { y: number; m: number; d: number } | null {
    if (!s) return null;
    // Accept plain date 'YYYY-MM-DD' or ISO datetime starting with that date
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (!m) return null;
    const y = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);
    if (!y || !mm || !dd) return null;
    return { y, m: mm, d: dd };
  }

  function setDobFromDateStr(s?: string) {
    const p = parseYmd(s || null);
    if (!p) { setDobParts({ day: '', month: '', year: '' }); return; }
    setDobParts({ day: String(p.d), month: String(p.m), year: String(p.y) });
  }

  function formatDobPreview(parts: { day: string; month: string; year: string }): string {
    const dd = parts.day ? parts.day.toString().padStart(2, '0') : '__';
    const mm = parts.month ? parts.month.toString().padStart(2, '0') : '__';
    const yyyy = parts.year || '____';
    return `${dd}.${mm}.${yyyy}`;
  }

  function calculateAgeFromParts(parts: { day: string; month: string; year: string }): number | null {
    if (!parts.day || !parts.month || !parts.year) return null;
    const y = Number(parts.year);
    const m = Number(parts.month);
    const d = Number(parts.day);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    const today = new Date();
    let age = today.getFullYear() - y;
    const month = today.getMonth() + 1;
    const day = today.getDate();
    if (month < m || (month === m && day < d)) age--;
    return age;
  }

  // Czech pluralization for years: 1 rok, 2–4 roky, 5+ let (11–14 let)
  function czYears(n: number): string {
    if (n === 1) return 'rok';
    if (n >= 2 && n <= 4) return 'roky';
    return 'let';
  }

  // Update DOB parts and, when complete, compose YYYY-MM-DD. Clamp day to month length.
  function updateDobPart(part: 'day'|'month'|'year', value: string) {
    setDobParts((prev) => {
      let next = { ...prev, [part]: value } as { day: string; month: string; year: string };
      // Clamp day if month/year present
      const y = Number(next.year || '0');
      const m = Number(next.month || '0');
      const d = Number(next.day || '0');
      if (y && m && d) {
        const maxDay = new Date(y, m, 0).getDate();
        if (d > maxDay) {
          next.day = String(maxDay);
        }
      }
      // Apply to editing only when all three are set
      const yy = Number(next.year || '0');
      const mm = Number(next.month || '0');
      const dd = Number(next.day || '0');
      if (yy && mm && dd) {
        const ymd = `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        setEditing((p) => ({ ...(p as any), date_of_birth: ymd }));
      } else {
        setEditing((p) => ({ ...(p as any), date_of_birth: '' }));
      }
      return next;
    });
  }

};

export default PlayersAdminPage;
